/**
 * Pure helpers for the URL-driven navigation model. The URL is the source of
 * truth for *which* brand/theme is being edited:
 *
 *   /b/:brandId/theming?theme=:themeId
 *   /b/:brandId/terminology | games | members | demo | places
 *   /b/:brandId/p/:placeId            (one place, today)
 *
 * `BrandRoute` reads these off the route and syncs them into `PortalContext`;
 * the switchers navigate rather than mutate state. Keeping the parsing here (a
 * dependency-free module) makes the rules unit-testable and shared between the
 * router, the blocker, and the sidebar.
 */
import type { Brand } from "@/data/config";

/** Editor sections that live under `/b/:brandId`. */
export const BRAND_SECTIONS = ["theming", "terminology", "games", "members", "demo", "places"] as const;
export type BrandSection = (typeof BRAND_SECTIONS)[number];

/** Default section a brand opens on (and the one switchers land on). */
export const DEFAULT_SECTION: BrandSection = "theming";

// `p/:placeId` counts as the places section (a place opened from the list).
// `p` only matches when a `/` follows, so `/b/x/p` and `/b/x/p-foo` do not.
const BRAND_PATH = /^\/b\/([^/]+)\/(theming|terminology|games|members|demo|places|p(?=\/))\b/;

/** Build a brand-scoped editor path, optionally pinning a theme via `?theme`. */
export function brandPath(brandId: string, section: BrandSection = DEFAULT_SECTION, themeId?: string | null): string {
  const base = `/b/${brandId}/${section}`;
  return themeId ? `${base}?theme=${encodeURIComponent(themeId)}` : base;
}

/**
 * Where an authenticated user with no specific destination should land.
 * Mirrors the access axes: edit a brand if you have one, else create one, else
 * (super admins with no brands of their own) the cross-brand directory.
 */
export function landingPath(opts: {
  brands: Pick<Brand, "id">[];
  canCreateBrand: boolean;
  isSuperAdmin: boolean;
}): string {
  const first = opts.brands[0];
  if (first) return brandPath(first.id);
  if (opts.canCreateBrand) return "/create";
  if (opts.isSuperAdmin) return "/admin/brands";
  // No brands and can't create — the access guard shows the partners-only page;
  // /create is a harmless fallback that the guard will pre-empt anyway.
  return "/create";
}

export interface EditTarget {
  /** Brand id from the path, or null when not on a brand-editor route. */
  brandId: string | null;
  /** Theme id from `?theme`, or null when unset (means "the live theme"). */
  themeId: string | null;
  /** Whether this location is one of the brand editor sections. */
  inEditor: boolean;
}

/**
 * Parse the edited target out of a location. Used by the unsaved-changes
 * blocker to decide whether a navigation changes *what* is being edited (brand
 * or theme) versus merely the section — and by BrandRoute to read the route.
 */
export function editTarget(loc: { pathname: string; search: string }): EditTarget {
  const match = loc.pathname.match(BRAND_PATH);
  const params = new URLSearchParams(loc.search);
  return {
    brandId: match?.[1] ?? null,
    themeId: params.get("theme"),
    inEditor: match !== null,
  };
}

/** Extract the active section from a pathname, defaulting to the editor home. */
export function sectionOf(pathname: string): BrandSection {
  const match = pathname.match(BRAND_PATH);
  if (match?.[2] === "p") return "places";
  return (match?.[2] as BrandSection) ?? DEFAULT_SECTION;
}

/** The list of places, under a brand. */
export function placesPath(brandId: string): string {
  return brandPath(brandId, "places");
}

/** One place ("the place today"), opened under a brand. The place need not be
 *  the brand's own: staff open house places from the same list. */
export function placePath(brandId: string, placeId: string): string {
  return `/b/${brandId}/p/${encodeURIComponent(placeId)}`;
}
